// Cloudflare Turnstile widget (bot check before creating or joining a room).
// The script must come from challenges.cloudflare.com: Turnstile cannot be bundled, so this
// is the one documented exception to "no external scripts" (see README.md).

import { LANG } from "./i18n";

interface TurnstileRenderOptions {
  sitekey: string;
  appearance?: "always" | "execute" | "interaction-only";
  language?: string;
  callback(token: string): void;
  "expired-callback"?(): void;
  "error-callback"?(code: string): boolean | void;
  "before-interactive-callback"?(): void;
  "after-interactive-callback"?(): void;
}

interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TOKEN_TIMEOUT_MS = 30_000;

let loading: Promise<TurnstileApi> | null = null;

function loadScript(): Promise<TurnstileApi> {
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.addEventListener("load", () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile did not initialize")),
    );
    script.addEventListener("error", () => {
      loading = null; // allow a later retry
      reject(new Error("Turnstile script failed to load"));
    });
    document.head.append(script);
  });
  return loading;
}

/**
 * One invisible-unless-needed widget shared by every protected action on a page. Tokens are
 * single-use, so each action takes one with `take()` and calls `reset()` afterwards.
 */
export class TurnstileGate {
  private api: TurnstileApi | null = null;
  private widgetId: string | null = null;
  private token: string | null = null;
  private waiters: { resolve(token: string): void; reject(error: Error): void }[] = [];

  /**
   * `onInteractive(true)` fires when Cloudflare wants a click on the checkbox (the widget is
   * otherwise invisible), and `onInteractive(false)` once it is done.
   */
  constructor(
    private readonly container: HTMLElement,
    private readonly siteKey: string,
    private readonly onInteractive: (needed: boolean) => void = () => {},
  ) {}

  async mount(): Promise<void> {
    this.api = await loadScript();
    this.widgetId = this.api.render(this.container, {
      sitekey: this.siteKey,
      // Only shows a checkbox when Cloudflare actually needs the user to interact.
      appearance: "interaction-only",
      language: LANG,
      callback: (token) => {
        this.token = token;
        this.onInteractive(false);
        for (const waiter of this.waiters.splice(0)) waiter.resolve(token);
      },
      "expired-callback": () => {
        this.token = null;
      },
      "error-callback": (code) => {
        // Fail the waiting action now instead of after the timeout; Turnstile retries on its own.
        console.warn("[Turnstile] error", code);
        this.token = null;
        for (const waiter of this.waiters.splice(0)) waiter.reject(new Error(`Turnstile error ${code}`));
      },
      "before-interactive-callback": () => {
        this.onInteractive(true);
        this.container.scrollIntoView({ block: "center", behavior: "smooth" });
      },
      "after-interactive-callback": () => this.onInteractive(false),
    });
  }

  /** Resolves with an unused token, waiting for the challenge to finish if needed. */
  take(): Promise<string> {
    const ready = this.token;
    if (ready) {
      this.token = null;
      return Promise.resolve(ready);
    }
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.waiters = this.waiters.filter((w) => w !== waiter);
        // Start a fresh challenge so the next attempt doesn't wait on a stuck one.
        this.reset();
        reject(new Error("Turnstile timed out"));
      }, TOKEN_TIMEOUT_MS);
      const waiter = {
        resolve: (token: string) => {
          clearTimeout(timer);
          this.token = null;
          resolve(token);
        },
        reject: (error: Error) => {
          clearTimeout(timer);
          reject(error);
        },
      };
      this.waiters.push(waiter);
      // The checkbox may already be showing below the fold.
      if (this.container.querySelector("iframe") || this.container.offsetHeight > 0) {
        this.container.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    });
  }

  /** Requests a fresh token (the previous one was spent or rejected). */
  reset(): void {
    this.token = null;
    if (this.api && this.widgetId) this.api.reset(this.widgetId);
  }
}
